/**
 * Collect all files from a drag-and-drop DataTransfer, including nested folders
 * (Chrome/Edge/Safari via webkitGetAsEntry). Falls back to dataTransfer.files.
 */

interface FileSystemFileEntryLike {
  isFile: true;
  isDirectory: false;
  file: (success: (f: File) => void, error?: (e: Error) => void) => void;
}

interface FileSystemDirectoryEntryLike {
  isFile: false;
  isDirectory: true;
  createReader: () => DirectoryReaderLike;
}

type EntryLike = FileSystemFileEntryLike | FileSystemDirectoryEntryLike;

interface DirectoryReaderLike {
  readEntries: (success: (entries: EntryLike[]) => void, error?: (e: Error) => void) => void;
}

function readAllDirectoryEntries(reader: DirectoryReaderLike): Promise<EntryLike[]> {
  return new Promise((resolve, reject) => {
    const acc: EntryLike[] = [];
    const read = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(acc);
            return;
          }
          acc.push(...batch);
          read();
        },
        (err) => reject(err)
      );
    };
    read();
  });
}

async function traverseEntry(entry: EntryLike | null, out: File[]): Promise<void> {
  if (!entry) return;
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => {
      entry.file(resolve, reject);
    });
    out.push(file);
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const children = await readAllDirectoryEntries(reader);
    for (const child of children) {
      await traverseEntry(child, out);
    }
  }
}

function getWebkitEntry(item: DataTransferItem): EntryLike | null {
  const w = item as DataTransferItem & { webkitGetAsEntry?: () => unknown };
  if (typeof w.webkitGetAsEntry === 'function') {
    try {
      return w.webkitGetAsEntry() as EntryLike | null;
    } catch {
      return null;
    }
  }
  return null;
}

export async function collectFilesFromDataTransfer(dataTransfer: DataTransfer): Promise<File[]> {
  const collected: File[] = [];

  if (dataTransfer.items && dataTransfer.items.length > 0) {
    const entries: (EntryLike | null)[] = [];
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];
      if (item.kind === 'file') {
        entries.push(getWebkitEntry(item));
      }
    }
    for (const ent of entries) {
      await traverseEntry(ent, collected);
    }
    if (collected.length > 0) {
      return collected;
    }
  }

  return Array.from(dataTransfer.files || []);
}
